<script setup lang="ts">
import { ref, computed, watch, useEventListener, useDayjs, definePageMeta, usePreviewBridge } from '#imports'
import { parseMarkdown } from 'comark'
import type { MarkdownDocument } from 'comark'
import { blogParseOptions } from '@core/utils/blogParseOptions'
import type { BlogPostWithTree } from '@core/types/blog'
import { z } from 'zod/v4'

definePageMeta({
    layout: false,
    hideOverlayContainer: true,
    preview: true,
})

const DataSchema = z.looseObject({
    md: z.string().default(''),
    title: z.string().default(''),
    subtitle: z.string().default(''),
    label: z.string().default('Blog post'),
    heroUrl: z.string().default(''),
})

const { data } = usePreviewBridge(DataSchema)
const dayjs = useDayjs()

const tree = ref<MarkdownDocument>()
let parseSequence = 0
watch(() => (data.value?.success ? data.value.data.md : undefined), async (md) => {
    if (md === undefined) return
    const sequence = ++parseSequence
    const parsed = await parseMarkdown(md, blogParseOptions)
    if (sequence === parseSequence) tree.value = parsed
}, { immediate: true })

const post = computed<BlogPostWithTree | null>(() => {
    if (!data.value?.success || !tree.value) return null
    const d = data.value.data
    return {
        id: 0,
        slug: 'preview',
        title: d.title,
        subtitle: d.subtitle,
        label: d.label as BlogPostWithTree['label'],
        publish_at: dayjs().toISOString(),
        hero_url: d.heroUrl,
        seo_meta_title: null,
        seo_meta_description: null,
        tree: tree.value,
        related: [],
    }
})

// Capture-phase so NuxtLink never navigates the preview iframe away.
if (import.meta.client) {
    useEventListener(window, 'click', (event: MouseEvent) => {
        const anchor = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[href]')
        if (!anchor?.getAttribute('href')) return
        event.preventDefault()
        event.stopPropagation()
    }, { capture: true })
}
</script>

<template>
    <UIApp>
        <div v-if="!post" class="preview-waiting">
            Waiting for content…
        </div>
        <NimbusBlogDetails
            v-else
            :post="post"
        />
    </UIApp>
</template>

<style lang="scss">
body {
    background-color: var(--surface, $white);
}
</style>

<style scoped lang="scss">
.preview-waiting {
    padding: 16px;
    color: var(--panel-color, $panel-font-color);
}
</style>
